import { useEffect, useState } from "react";
import { ArrowRight, Target, Gem, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_2011e831-9e14-4050-89e9-67f554973600/artifacts/1oxcap9f_Logo%20The%20Rig.svg";
const VALUE_ICONS = [Target, Gem, Users];
const VALUE_COLORS = ["#FF6B2B", "#2DC653", "#0A6E6E"];

export default function About() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [footer, setFooter] = useState(null);

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => {
      setContent(r.data.about_page || {});
      setFooter(r.data.footer || {});
    }).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = content?.hero_title ? `About - ${content.hero_title}` : "About Us | The Rig - Coworking & Meeting Spaces";
  }, [content]);

  if (!content) return (
    <div className="max-w-5xl mx-auto px-6 py-24">
      <title>About Us | The Rig - Coworking & Meeting Spaces</title>
      <meta name="description" content="Learn about The Rig — a purpose-built coworking space offering flexible meeting rooms and offices for modern professionals." />
      <meta property="og:title" content="About The Rig - Coworking & Meeting Spaces" />
      <meta property="og:description" content="Flexible, technology-enabled workspaces that help teams do their best work." />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://customer-assets.emergentagent.com/job_book-spaces-2/artifacts/2dhwnmm0_therig.png" />
      <div className="h-64 bg-gray-100 animate-pulse rounded-2xl" />
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <title>{content?.hero_title ? `About ${content.hero_title} | The Rig` : "About Us | The Rig - Coworking & Meeting Spaces"}</title>
      <meta name="description" content={content?.story_text?.substring(0, 160) || "Learn about The Rig — a purpose-built coworking space offering flexible meeting rooms and offices for modern professionals."} />
      <meta property="og:title" content={content?.hero_title ? `About ${content.hero_title} - Coworking & Meeting Spaces` : "About The Rig - Coworking & Meeting Spaces"} />
      <meta property="og:description" content={content?.mission_text?.substring(0, 160) || "Flexible, technology-enabled workspaces that help teams do their best work."} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={content?.hero_image || "https://customer-assets.emergentagent.com/job_book-spaces-2/artifacts/2dhwnmm0_therig.png"} />

      {/* Hero */}
      <section className="relative h-[50vh] overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img src={content.hero_image || ""} alt="About The Rig" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30" />
        </div>
        <div className="absolute top-0 left-0 w-full flex h-1 z-10">
          <div className="flex-1" style={{ backgroundColor: "#FFD60A" }} />
          <div className="flex-1" style={{ backgroundColor: "#FF6B2B" }} />
          <div className="flex-1" style={{ backgroundColor: "#2DC653" }} />
          <div className="flex-1" style={{ backgroundColor: "#0A6E6E" }} />
        </div>
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-end pb-16">
          <span className="inline-block bg-rig-orange/20 text-rig-orange text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4 w-fit">
            {content.hero_subtitle || "About Us"}
          </span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white">{content.hero_title || "About The Rig"}</h1>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32" data-testid="about-story">
        <div className="max-w-4xl mx-auto px-6">
          <span className="inline-block bg-rig-teal/10 text-rig-teal text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
            Our Story
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-rig-charcoal mb-8">{content.story_title || ""}</h2>
          <div className="text-rig-charcoal/60 leading-relaxed whitespace-pre-line text-base md:text-lg">
            {content.story_text || ""}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 md:py-24" style={{ backgroundColor: "#1E1E1E" }} data-testid="about-mission">
        <div className="flex h-1 absolute left-0 right-0">
          <div className="flex-1" style={{ backgroundColor: "#FFD60A" }} />
          <div className="flex-1" style={{ backgroundColor: "#FF6B2B" }} />
          <div className="flex-1" style={{ backgroundColor: "#2DC653" }} />
          <div className="flex-1" style={{ backgroundColor: "#0A6E6E" }} />
        </div>
        <div className="max-w-4xl mx-auto px-6">
          <span className="inline-block bg-rig-yellow/20 text-rig-yellow text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
            Our Mission
          </span>
          <h2 className="font-heading font-bold text-3xl text-white mb-6">{content.mission_title || "Our Mission"}</h2>
          <p className="text-white/50 text-lg leading-relaxed">{content.mission_text || ""}</p>
        </div>
      </section>

      {/* Values */}
      {content.values && content.values.length > 0 && (
        <section className="py-24 md:py-32" style={{ backgroundColor: "#FFF8E7" }} data-testid="about-values">
          <div className="max-w-7xl mx-auto px-6">
            <span className="inline-block bg-rig-green/10 text-rig-green text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
              What Drives Us
            </span>
            <h2 className="font-heading font-bold text-3xl text-rig-charcoal mb-14">Our Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {content.values.map((v, i) => {
                const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
                const color = VALUE_COLORS[i % VALUE_COLORS.length];
                return (
                  <div
                    key={i}
                    className="bg-white rounded-2xl p-8 shadow-md border-l-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    style={{ borderLeftColor: color }}
                    data-testid={`about-value-${i}`}
                  >
                    <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl mb-5" style={{ backgroundColor: `${color}15` }}>
                      <Icon className="h-6 w-6" style={{ color }} strokeWidth={2} />
                    </div>
                    <h3 className="font-heading font-bold text-xl mb-2 text-rig-charcoal">{v.title}</h3>
                    <p className="text-rig-charcoal/50 text-sm leading-relaxed">{v.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: "#1E1E1E" }} data-testid="about-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-white mb-4">Come see for yourself</h2>
          <p className="text-white/40 mb-8">Book a tour or browse our available spaces.</p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Button onClick={() => navigate("/spaces")} className="bg-rig-orange text-white hover:bg-rig-orange/90 rounded-full px-8 py-3 font-bold shadow-lg shadow-rig-orange/25" data-testid="about-browse-btn">
              Browse Spaces <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => navigate("/contact")} className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-3 font-bold bg-transparent" data-testid="about-contact-btn">
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: "#1E1E1E" }}>
        <div className="flex h-1">
          <div className="flex-1" style={{ backgroundColor: "#FFD60A" }} />
          <div className="flex-1" style={{ backgroundColor: "#FF6B2B" }} />
          <div className="flex-1" style={{ backgroundColor: "#2DC653" }} />
          <div className="flex-1" style={{ backgroundColor: "#0A6E6E" }} />
        </div>
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div>
              <img src={LOGO_URL} alt="The Rig" className="h-12 invert mb-4" />
              <p className="text-white/40 text-sm max-w-xs leading-relaxed">{footer?.description || ""}</p>
            </div>
            <div>
              <h4 className="text-white font-heading font-bold text-sm uppercase tracking-wider mb-4">Quick Links</h4>
              <nav className="flex flex-col gap-2.5">
                {[{ to: "/", label: "Home" }, { to: "/spaces", label: "Spaces" }, { to: "/about", label: "About" }, { to: "/contact", label: "Contact" }].map(link => (
                  <Link key={link.to} to={link.to} className="text-white/40 hover:text-rig-yellow text-sm transition-colors">{link.label}</Link>
                ))}
              </nav>
            </div>
            <div>
              <h4 className="text-white font-heading font-bold text-sm uppercase tracking-wider mb-4">Contact</h4>
              <p className="text-white/40 text-sm">{footer?.email || "therig@ic-foundation.org"}</p>
            </div>
          </div>
          <div className="mt-14 pt-8 border-t border-white/10 text-white/25 text-xs">{footer?.copyright || ""}</div>
        </div>
      </footer>
    </div>
  );
}
